import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    moveType: '',
    moveDate: '',
    homeSize: '',
    movingFrom: '',
    movingTo: '',
    additionalInfo: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.fullName || !formData.email || !formData.phone) {
      toast({
        title: "Required Fields Missing",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit to backend API
      const response = await axios.post(`${API}/quotes`, formData);
      
      console.log('Quote request created:', response.data);
      
      toast({
        title: "Quote Request Submitted!",
        description: "We'll contact you within 24 hours with your free quote.",
      });
      
      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        moveType: '',
        moveDate: '',
        homeSize: '',
        movingFrom: '',
        movingTo: '',
        additionalInfo: ''
      });
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "There was an error submitting your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Get Your Free, No-Obligation Quote
          </h2>
          <p className="text-lg text-gray-600">
            Tell us about your move and we'll provide a transparent, competitive quote within 24 hours
          </p>
        </div>
        
        <div className="bg-white rounded-lg shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                required
                value={formData.fullName}
                onChange={handleInputChange}
                className="w-full"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="moveType" className="block text-sm font-medium text-gray-700 mb-2">
                  Type of Move
                </label>
                <Select
                  value={formData.moveType}
                  onValueChange={(value) => handleSelectChange('moveType', value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select move type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="local">Local Move</SelectItem>
                    <SelectItem value="long-distance">Long Distance</SelectItem>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial/Office</SelectItem>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="senior">Senior Moving</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label htmlFor="moveDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Move Date
                </label>
                <Input
                  id="moveDate"
                  name="moveDate"
                  type="date"
                  value={formData.moveDate}
                  onChange={handleInputChange}
                  className="w-full"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="homeSize" className="block text-sm font-medium text-gray-700 mb-2">
                Home/Office Size
              </label>
              <Select
                value={formData.homeSize}
                onValueChange={(value) => handleSelectChange('homeSize', value)}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="studio">Studio/1 Bedroom</SelectItem>
                  <SelectItem value="2bed">2 Bedroom</SelectItem>
                  <SelectItem value="3bed">3 Bedroom</SelectItem>
                  <SelectItem value="4bed">4+ Bedroom</SelectItem>
                  <SelectItem value="small-office">Small Office</SelectItem>
                  <SelectItem value="large-office">Large Office</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="movingFrom" className="block text-sm font-medium text-gray-700 mb-2">
                  Moving From (City, State)
                </label>
                <Input
                  id="movingFrom"
                  name="movingFrom"
                  type="text"
                  value={formData.movingFrom}
                  onChange={handleInputChange}
                  className="w-full"
                />
              </div>
              
              <div>
                <label htmlFor="movingTo" className="block text-sm font-medium text-gray-700 mb-2">
                  Moving To (City, State)
                </label>
                <Input
                  id="movingTo"
                  name="movingTo"
                  type="text"
                  value={formData.movingTo}
                  onChange={handleInputChange}
                  className="w-full"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Information
              </label>
              <Textarea
                id="additionalInfo"
                name="additionalInfo"
                rows={4}
                value={formData.additionalInfo}
                onChange={handleInputChange}
                className="w-full"
              />
            </div>
            
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-brand-blue hover:bg-brand-blue-700 text-white py-6 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Request Free Quote'}
            </Button>
            
            <div className="text-center mt-6">
              <p className="text-gray-600 mb-4">Or prefer to speak with us directly?</p>
              <a
                href="tel:+18624185062"
                className="inline-flex items-center justify-center gap-2 bg-white border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white transition-colors px-8 py-4 rounded-md font-semibold text-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call Us: +1 (862) 418-5062
              </a>
            </div>
            
            <p className="text-sm text-gray-500 text-center mt-6">
              By submitting this form, you agree to be contacted by ATW Against the Wind Moving Services
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;